import "./Navbar.scss"

import { FC, useState, useRef, useMemo, MouseEvent } from "react"
import { NavLink } from "react-router-dom"
import cn from "classnames"

import { useNavbarStore } from "stores/navbar"
import { NavbarState } from "config/constants"

export type NavbarItemProps = {
    label: string
    route?: string
    action?: Function
    hasNavbarCloseIcon?: boolean
    showOnStateOnly?: NavbarState
    iconView?: React.JSX.Element
    iconImg?: string
    badge?: React.JSX.Element
    isTopLevel?: boolean
    className?: string
    hidden?: boolean
    items?: (NavbarItemProps | null)[]
}

const NavbarItem: FC<React.PropsWithChildren<NavbarItemProps>> = ({
    label,
    route,
    action,
    hasNavbarCloseIcon = false,
    iconView,
    iconImg,
    badge,
    isTopLevel,
    className,
    hidden,
    items: itemsProp,
}) => {
    const [isItemExpanded, setIsItemExpanded] = useState(false)
    const {
        navbarState,
        forceHideSubmenus,
        collapseNavbar,
        setForceHideSubmenus
    } = useNavbarStore()
    const navbarItemRef = useRef<HTMLDivElement>(null)
    const items = useMemo(() => itemsProp?.filter(i => !!i && !i.hidden) as (NavbarItemProps[] | undefined), [itemsProp])
    const hasSubItems = !!items && items.length > 0

    const shouldShowSubItemsOnExpanded = useMemo(() => {
        if (items?.length === 1) {
            return !!items[0] && items[0].showOnStateOnly !== NavbarState.collapsed
        }
        return hasSubItems
    }, [hasSubItems, items])

    const onArrowClick = (e: MouseEvent) => {
        e.stopPropagation()
        e.preventDefault()
        setIsItemExpanded(!isItemExpanded)
    }

    const onCloseClick = (e: MouseEvent) => {
        e.stopPropagation()
        e.preventDefault()
        collapseNavbar()
    }

    const onItemClick = (e: MouseEvent) => {
        if (typeof action === "function") {
            action()
        }
        if (!isTopLevel && navbarState === NavbarState.collapsed) {
            setForceHideSubmenus(true)
            setTimeout(() => {
                setForceHideSubmenus(false)
            })
        }
    }

    const badgeNode = (
        <div className="navbar__item-badge">
            {badge}
        </div>
    )

    const navItemContent = (
        <>
            {(iconView || iconImg) &&
                <div className="navbar__item-icon">
                    {iconView}
                    {!iconView && iconImg &&
                        <img src={iconImg} alt="" />
                    }
                    {!!badge && navbarState === NavbarState.collapsed &&
                        badgeNode
                    }
                </div>
            }
            <div className="navbar__item--label">
                {label}
            </div>
            {!!badge && navbarState === NavbarState.expanded &&
                badgeNode
            }
            {hasSubItems && shouldShowSubItemsOnExpanded &&
                <div className="navbar__item--arrow" onClick={onArrowClick}></div>
            }
            {hasNavbarCloseIcon && navbarState === NavbarState.expanded &&
                <div className="navbar__item--close" onClick={onCloseClick}></div>
            }
        </>
    )

    const renderItems = (itemsList?: (NavbarItemProps | null)[]) => {
        return itemsList ? itemsList.filter(i => {
            if (!i) return false
            if (!i.showOnStateOnly) {
                return true
            }
            return i.showOnStateOnly === navbarState
        }).map((item: NavbarItemProps | null, index: number) => {
            if (!item) return null
            return <NavbarItem
                {...item}
                key={index} />
        }) : null
    }

    return (
        <div ref={navbarItemRef}
            className={cn(
                "navbar__item",
                className,
                { "navbar__item--is-expanded" : isItemExpanded },
            )}>
            {!!route ?
                <NavLink
                    to={route}
                    className={({ isActive }) => (
                        `navbar__item-body${isTopLevel ? " navbar__item-body--top-level" : ""}${isActive ? " navbar__item-body--selected" : ""}`
                    )}
                    onClick={onItemClick}>
                    {navItemContent}
                </NavLink>
            :
                <div className={"navbar__item-body" + (isTopLevel ? " navbar__item-body--top-level" : "")}
                    onClick={onItemClick}>
                    {navItemContent}
                </div>
            }
            {hasSubItems && !forceHideSubmenus &&
                <div className="navbar__sub">
                    {renderItems(items)}
                </div>
            }
        </div>
    )
};

export default NavbarItem
